<template>
  <div class="user-management" dir="rtl">
    <div class="container">
      <!-- Header Section -->
      <header class="header">
        <h1 class="page-title">إدارة المستخدمين</h1>
        <p class="page-subtitle">قم بإدارة حسابات المستخدمين بسهولة</p>
      </header>

      <!-- Users Table -->
      <div class="table-container">
        <!-- Table Search and Filters -->
        <div class="table-header">
          <h2 class="table-title">قائمة المستخدمين</h2>
          <div class="table-actions">
            <div class="search-container">
              <input
                type="text"
                v-model="searchQuery"
                placeholder="بحث..."
                class="search-input"
              />
              <span class="search-icon" @click="search()">🔍</span>
            </div>
          </div>
        </div>

        <!-- Users Table -->
        <div class="table-responsive">
          <table class="users-table table table-striped table-hover table-sm">
            <thead>
              <tr>
                <th>التسلسل</th>
                <th>الاسم الكامل</th>
                <th>البريد الإلكتروني</th>
                <th>رقم الهاتف</th>
                <th>الجامعة</th>
                <th>الكلية</th>
                <th>المستوى الأكاديمي</th>
                <th>الرصيد</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(user, index) in users"
                :key="user.id"
                :class="{ 'is-deleting': user.isDeleting }"
              >
                <td>{{ (pageNumber - 1) * pageSize + (index + 1) }}</td>
                <td>
                  <div class="user-info">
                    <span class="user-name">{{ user.fullName }}</span>
                  </div>
                </td>
                <td>{{ user.email }}</td>
                <td>{{ user.phoneNumber }}</td>
                <td>{{ user.university }}</td>
                <td>{{ user.faculty }}</td>
                <td>
                  <span class="academic-level">{{ user.academicLevel }}</span>
                </td>
                <td>
                  <span class="academic-level">{{
                    user.wallet != null ? user.wallet.points : 0
                  }}</span>
                </td>
                <td>
                  <div class="table-actions">
                    <button
                      class="btn-action edit"
                      @click="openUserModalForEdit(user)"
                    >
                      <span class="action-icon">✎</span>
                      تعديل
                    </button>
                    <button
                      class="btn-action delete"
                      @click="deleteUser(user.id)"
                    >
                      <span class="action-icon">×</span>
                      حذف
                    </button>
                    <button
                      class="btn-action point"
                      @click="openPointModal(user)"
                    >
                      <span class="action-icon">+</span>
                      نقاط
                    </button>
          <!-- resetPassword -->
                    <button
                      class="btn-action point"
                      @click="resetPassword(user.id)"
                    >
                      <span class="action-icon">🔒</span>
                      تصفير
                    </button>

                  </div>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="users.length === 0">
                <td colspan="12" class="empty-state">
                  <div class="empty-icon">👥</div>
                  <h3>لا يوجد مستخدمين</h3>
                  <p>قم بإضافة مستخدم جديد للبدء</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-container">
          <custom-pagination
            :currentPage="pageNumber"
            :totalPages="totalPages"
            @page-changed="handlePageChange"
          />
        </div>
      </div>

      <transition name="modal">
        <div
          v-if="showUserModal"
          class="modal-overlay"
          @click.self="closeUserModal"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">
                {{ isEditMode ? "تعديل المستخدم" : "إضافة مستخدم جديد" }}
              </h2>
              <button class="modal-close" @click="closeUserModal">
                &times;
              </button>
            </div>

            <form @submit.prevent="handleUserSubmit" class="user-form">
              <div class="modal-body">
                <!-- Personal Information -->
                <div class="form-row">
                  <div class="form-group col-6">
                    <label class="form-label" for="fullName"
                      >الاسم الكامل</label
                    >
                    <input
                      type="text"
                      id="fullName"
                      v-model="userForm.fullName"
                      class="form-control"
                      placeholder="أدخل الاسم الكامل"
                      required
                    />
                  </div>

                  <div class="form-group col-6">
                    <label class="form-label" for="email"
                      >البريد الإلكتروني</label
                    >
                    <input
                      type="email"
                      id="email"
                      v-model="userForm.email"
                      class="form-control"
                      placeholder="أدخل البريد الإلكتروني"
                      required
                    />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-6">
                    <label class="form-label" for="phoneNumber"
                      >رقم الهاتف</label
                    >
                    <input
                      type="tel"
                      id="phoneNumber"
                      v-model="userForm.phoneNumber"
                      class="form-control"
                      placeholder="أدخل رقم الهاتف"
                      required
                    />
                  </div>
                </div>

                <!-- Academic Information -->
                <div class="form-row">
                  <div class="form-group col-4">
                    <label class="form-label" for="university">الجامعة</label>
                    <input
                      type="text"
                      id="university"
                      v-model="userForm.university"
                      class="form-control"
                      placeholder="أدخل اسم الجامعة"
                      required
                    />
                  </div>

                  <div class="form-group col-4">
                    <label class="form-label" for="faculty">الكلية</label>
                    <input
                      type="text"
                      id="faculty"
                      v-model="userForm.faculty"
                      class="form-control"
                      placeholder="أدخل اسم الكلية"
                      required
                    />
                  </div>

                  <div class="form-group col-4">
                    <label class="form-label" for="academicLevel"
                      >المستوى الأكاديمي</label
                    >
                    <input
                      type="text"
                      id="academicLevel"
                      v-model="userForm.academicLevel"
                      class="form-control"
                      placeholder="أدخل المستوى الأكاديمي"
                      required
                    />
                  </div>
                </div>

                <!-- Password Fields -->
                <div class="form-row">
                  <!-- deviceType  -->
                  <div class="form-group col-6">
                    <label class="form-label" for="deviceType"
                      >نوع الجهاز</label
                    >
                    <select
                      id="deviceType"
                      v-model="userForm.deviceType"
                      class="form-control"
                      required
                    >
                      <option value="Android">أندرويد</option>
                      <option value="IOS">iOS</option>
                    </select>
                  </div>

                  <!-- deviceId -->
                  <div class="form-group col-6">
                    <label class="form-label" for="deviceId">رقم الجهاز</label>
                    <input
                      type="text"
                      id="deviceId"
                      v-model="userForm.deviceId"
                      class="form-control"
                      placeholder="أدخل رقم الجهاز"
                      required
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-6">
                    <label class="form-label" for="password">كلمة المرور</label>
                    <div class="password-input">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        id="password"
                        v-model="userForm.password"
                        class="form-control"
                        :placeholder="
                          isEditMode
                            ? 'اتركه فارغاً للإبقاء على القديمة'
                            : 'أدخل كلمة المرور'
                        "
                        :required="!isEditMode"
                      />
                      <button
                        type="button"
                        class="toggle-password"
                        @click="showPassword = !showPassword"
                      >
                        {{ showPassword ? "👁️" : "👁️‍🗨️" }}
                      </button>
                    </div>
                  </div>

                  <div class="form-group col-6">
                    <label class="form-label" for="confirmPassword"
                      >تأكيد كلمة المرور</label
                    >
                    <div class="password-input">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        id="confirmPassword"
                        v-model="userForm.confirmPassword"
                        class="form-control"
                        :placeholder="
                          isEditMode
                            ? 'اتركه فارغاً للإبقاء على القديمة'
                            : 'أدخل تأكيد كلمة المرور'
                        "
                        :required="!isEditMode"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-actions">
                <button
                  type="submit"
                  class="btn-submit"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isEditMode ? "تحديث المستخدم" : "إضافة المستخدم" }}
                </button>
                <button
                  type="button"
                  class="btn-cancel"
                  @click="closeUserModal"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>
      <!-- Points Modal -->
      <transition name="modal">
        <div
          v-if="showPointModal"
          class="modal-overlay points-modal"
          @click.self="closePointModal"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">سحب او اضافة نقاط</h2>
              <button class="modal-close" @click="closePointModal">
                &times;
              </button>
            </div>

            <form @submit.prevent="handleAddPoint" class="points-form">
              <div class="form-group">
                <label class="form-label" for="points">عدد النقاط</label>
                <input
                  type="number"
                  id="points"
                  v-model.number="pointForm.points"
                  class="form-control"
                  placeholder="أدخل عدد النقاط"
                  min="0"
                  required
                />
              </div>
              <!-- سحب او اضافة -->

              <div class="form-group">
                <label for="role" class="form-label">نوع العملية</label>

                <select
                  id="role"
                  v-model.number="pointForm.transactionType"
                  class="form-control"
                  required
                >
                  <option value="" disabled>-- اختر نوع العملية --</option>
                  <option value="3">سحب</option>
                  <option value="4">ايداع</option>
                </select>
              </div>
              <!-- description -->

              <div class="form-group">
                <label class="form-label">ملاحظات</label>
                <textarea
                  v-model="pointForm.description"
                  class="form-control"
                  rows="4"
                  placeholder=""
                  required
                ></textarea>
              </div>

              <div class="modal-actions">
                <button
                  type="submit"
                  class="btn-submit"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="spinner"></span>
                  إضافة النقاط
                </button>
                <button
                  type="button"
                  class="btn-cancel"
                  @click="closePointModal"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import apiClient from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";
import Swal from "sweetalert2";

export default {
  name: "UserManagement",
  components: { CustomPagination },
  data() {
    return {
      users: [],
      showUserModal: false,
      isEditMode: false,
      showPointModal: false,
      selectedUserId: null,
      searchQuery: "",
      pageNumber: 1,
      pageSize: 8,
      totalPages: 1,

      userForm: {
        id: null,
        fullName: "",
        email: "",
        phoneNumber: "",
        university: "",
        faculty: "",
        academicLevel: "",
        password: "",
        confirmPassword: "",
        deviceType: "",
        deviceId: "",
      },

      pointForm: {
        points: 0,
        transactionType: "",
        description: "",
      },
    };
  },
  methods: {
   // search
    search() {
      this.pageNumber = 1;
      this.getAllUsers();
    },


    async getAllUsers() {
      try {
        const response = await apiClient.get("/api/Auth/GetAllUsers", {
          params: { PageNumber: this.pageNumber, PageSize: this.pageSize , SearchQuery: this.searchQuery },
        });
        this.users = response.data.items;
        this.pageNumber = response.data.pageNumber;
        this.totalPages = response.data.totalPages;
      } catch (error) {
        console.error("حدث خطأ أثناء جلب المستخدمين", error);
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "حدث خطأ أثناء جلب المستخدمين",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },


    // resetPassword
    async resetPassword(id) {
      const result = await Swal.fire({
        title: "تأكيد تصفير الحساب",
        text: "هل أنت متأكد من تصفير الحساب؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، تصفير الحساب",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        try {
          const response = await apiClient.put(
            "/api/Auth/ResetLogin/" + id
          );
          if (response) {
            Swal.fire({
            icon: "success",
            title: "تم",
            text: "تم تصفير الحساب بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
          this.getAllUsers();
          }
       
        } catch (error) {
          console.error("حدث خطأ أثناء تصفير الحساب", error);
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء تصفير الحساب",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    },

    handlePageChange(newPage) {
      this.pageNumber = newPage;
      this.getAllUsers();
    },

    async handleUserSubmit() {
      if (
        (this.userForm.password || this.userForm.confirmPassword) &&
        this.userForm.password !== this.userForm.confirmPassword
      ) {
        Swal.fire({
          icon: "warning",
          title: "تنبيه",
          text: "كلمة المرور وتأكيدها غير متطابقتين",
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }

      if (this.isEditMode) {
        try {
          const editPayload = {
            id: this.userForm.id,
            fullName: this.userForm.fullName,
            email: this.userForm.email,
            phoneNumber: this.userForm.phoneNumber,
            university: this.userForm.university,
            faculty: this.userForm.faculty,
            academicLevel: this.userForm.academicLevel,
            deviceType: this.userForm.deviceType,
            deviceId: this.userForm.deviceId,
          };
          if (this.userForm.password) {
            editPayload.password = this.userForm.password;
          }

          const response = await apiClient.put(
            "/api/Auth/EditUser/" + this.userForm.id,
            editPayload
          );
          Swal.fire({
            icon: "success",
            title: "تم",
            text: response.data.message || "تم تعديل المستخدم بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
          this.closeUserModal();
          this.getAllUsers();
        } catch (error) {
          console.error("حدث خطأ أثناء تعديل المستخدم", error);
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء تعديل المستخدم",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } else {
        try {
          const createPayload = {
            fullName: this.userForm.fullName,
            email: this.userForm.email,
            phoneNumber: this.userForm.phoneNumber,
            university: this.userForm.university,
            faculty: this.userForm.faculty,
            academicLevel: this.userForm.academicLevel,
            password: this.userForm.password,
            deviceType: this.userForm.deviceType,
            deviceId: this.userForm.deviceId,
          };
          const response = await apiClient.post(
            "/api/Auth/CreateUser",
            createPayload
          );
          Swal.fire({
            icon: "success",
            title: "تم",
            text: response.data.message || "تم إنشاء المستخدم بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
          this.closeUserModal();
          this.getAllUsers();
        } catch (error) {
          console.error("حدث خطأ أثناء إنشاء المستخدم", error);
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء إنشاء المستخدم",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    },

    openModalForAdd() {
      this.isEditMode = false;
      this.resetUserForm();
      this.showUserModal = true;
    },

    openUserModalForEdit(user) {
      this.isEditMode = true;
      this.userForm = {
        id: user.id,
        fullName: user.fullName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        university: user.university,
        faculty: user.faculty,
        academicLevel: user.academicLevel,
        password: "",
        confirmPassword: "",
        deviceId: user.deviceId,
        deviceType: user.deviceType,
      };
      this.showUserModal = true;
    },

    closeUserModal() {
      this.showUserModal = false;
      this.resetUserForm();
    },

    resetUserForm() {
      this.userForm = {
        id: null,
        fullName: "",
        email: "",
        phoneNumber: "",
        university: "",
        faculty: "",
        academicLevel: "",
        password: "",
        confirmPassword: "",
        deviceType: "",
        deviceId: "",
      };
    },

    async deleteUser(id) {
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        text: "هل أنت متأكد من حذف هذا المستخدم؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، احذفه",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        try {
          const response = await apiClient.delete("/api/Auth/DeleteUser/" + id);
          if(response){
          
          Swal.fire({
            icon: "success",
            title: "تم الحذف",
            text: "تم حذف المستخدم بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
        }
          this.getAllUsers();
        } catch (error) {
          console.error("حدث خطأ أثناء حذف المستخدم", error);
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء حذف المستخدم",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    },

    openPointModal(user) {
      this.selectedUserId = user.id;
      this.pointForm.points = 0;
      this.showPointModal = true;
    },

    closePointModal() {
      this.showPointModal = false;
      this.pointForm.points = 0;
      this.selectedUserId = null;
    },

    async handleAddPoint() {
      try {
        const response = await apiClient.post("/api/Auth/AddPoint", {
          userId: this.selectedUserId,
          points: this.pointForm.points,
          transactionType: this.pointForm.transactionType,
          description: this.pointForm.description,
        });
        if (response) {
          Swal.fire({
            icon: "success",
            title: "تم",
            text: "تم إضافة النقاط بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
        }
        this.getAllUsers();
        this.closePointModal();
      } catch (error) {
        console.error("حدث خطأ أثناء إضافة النقاط:", error);
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "حدث خطأ أثناء إضافة النقاط",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },
  },

  mounted() {
    this.getAllUsers();
  },
};
</script>

<style scoped>
.btn-action {
  margin: 3px;
}
.user-management {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Section */
.header {
  text-align: center;
  margin-bottom: 3rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: slideDown 0.5s ease-out;
}

.table-responsive {
  overflow-x: auto;
  white-space: nowrap;
}

.page-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-weight: 400;
}

.btn-add {
  background: #4299e1;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-add:hover {
  background: #3182ce;
  transform: translateY(-1px);
}

/* Table Styles */
.table-container {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.table-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 1.5rem;
  text-align: center;
}

.users-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.users-table th {
  background: #f7fafc;
  padding: 1rem;
  font-weight: 600;
  color: #4a5568;
  text-align: right;
  border-bottom: 2px solid #e2e8f0;
}

.users-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s;
}

.users-table tr:hover td {
  background-color: #f7fafc;
}

/* Button Styles */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-edit {
  background: #faf089;
  color: #744210;
}

.btn-edit:hover {
  background: #f6e05e;
}

.btn-delete {
  background: #fed7d7;
  color: #c53030;
}

.btn-delete:hover {
  background: #feb2b2;
}

.btn-point {
  background: #c6f6d5;
  color: #276749;
}

.btn-point:hover {
  background: #9ae6b4;
}

/* Modal Styles */
.modal-overlay {
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

/* Row Layout */
.row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem;
}

.col-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
  padding: 0.5rem;
}

.col-6 {
  flex: 0 0 50%;
  max-width: 50%;
  padding: 0.5rem;
}

.col-12 {
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0.5rem;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.btn-primary {
  background: #4299e1;
  color: white;
  padding: 0.75rem 1.5rem;
}

.btn-primary:hover {
  background: #3182ce;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

/* Points Modal */
.points-modal .modal-content {
  max-width: 400px;
}

/* Animations */
@keyframes modalSlideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .user-management {
    padding: 1rem;
  }

  .header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .col-4,
  .col-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .modal-content {
    padding: 1.5rem;
    margin: 1rem;
  }

  .table-container {
    overflow-x: auto;
  }

  .users-table {
    white-space: nowrap;
  }
}

/* RTL Support */
[dir="rtl"] .modal-close {
  left: 1rem;
  right: auto;
}

[dir="rtl"] .form-control {
  text-align: right;
}

[dir="rtl"] .modal-actions {
  flex-direction: row-reverse;
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

/* Form Styles */
.user-form {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: flex;
  margin: 0 -0.75rem;
  margin-bottom: 1.5rem;
}

.form-group {
  padding: 0 0.75rem;
}

.col-4 {
  width: 33.333333%;
}

.col-6 {
  width: 50%;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

/* Password Input */
.password-input {
  position: relative;
}

.toggle-password {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 0.25rem;
}

.toggle-password:hover {
  color: #4a5568;
}

/* Buttons */
.btn-submit {
  background: #4299e1;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-submit:hover:not(:disabled) {
  background: #3182ce;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

/* Loading Spinner */
.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal Animation */
@keyframes modalSlideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* RTL Specific */
[dir="rtl"] .modal-close {
  left: 1.5rem;
  right: auto;
}

[dir="rtl"] .toggle-password {
  left: auto;
  right: 0.75rem;
}

[dir="rtl"] .form-control {
  text-align: right;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .form-row {
    flex-direction: column;
  }

  .col-4,
  .col-6 {
    width: 100%;
    margin-bottom: 1rem;
  }
}
</style>
